/**
* Hand-authored per-locale copy for the /{locale}/moj pillar mirrors.
* Each locale ships unique intro, benefits, FAQ and deep-dive sections
* (>1,200 rendered words when combined with the pillar shell) so it
* satisfies E-E-A-T without machine translation. Locales: en zh ja ko vi id.
*/
export type MojLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface MojLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  faqs: Array<{ q: string; a: string }>;
  ctaCall: string;
  ctaLine: string;
  breadcrumbHome: string;
  breadcrumbMoj: string;
  seeAllServices: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
}

export const MOJ_LOCALE_COPY: Record<MojLocale, MojLocaleCopy> = {
  en: {
    hreflang: "en",
    title: "Thailand MoJ Certified Translation | Thai Notary Law",
    description:
      "MoJ-registered certified translation: court judgments, contracts, wills, corporate docs. 25 packages — contact our team for a quotation, 2–5 days, accepted by Thai courts.",
    h1: "Ministry of Justice Certified Translation — Registered Legal Translators",
    lead:
      "Thai Notary Law files certified translations through translators whose names appear on the roster of the Ministry of Justice's Rights and Liberties Protection Department. Every deliverable carries the translator's registration number, sworn certification statement and the firm's Notary Public Services attorney signature — the exact chain the Thai courts, Office of the Attorney General, Ministry of Foreign Affairs and destination-country consulates require. Over 12,000 pages delivered since 2019, covering litigation dossiers, cross-border contracts, corporate charters and inheritance instruments.",
    benefits: [
      ["Registered MoJ translators", "Translators on the active Ministry of Justice roster (Rights & Liberties Protection Department). Registration number printed on every page."],
      ["Court-grade certification", "Sworn translator statement + Notary Public attorney signature — accepted by Thai courts, prosecutors, land offices, and DBD."],
      ["Predictable scheduling", "Standard turnaround 2–5 business days, with rush handling available for hearing deadlines. Fees are quoted per case before work starts."],
      ["Legal-domain accuracy", "Bilingual lawyers review civil, criminal, commercial and family-law terminology; glossaries maintained per case."],
      ["Chain-of-custody workflow", "Every source file, translated draft and certification receipt is logged; you receive an audit-ready package."],
      ["Onward legalisation", "Attach MFA legalisation + destination-country embassy stamp in one quote when the document is going abroad."],
    ],
    faqs: [
      { q: "What is MoJ certified translation?", a: "A translation prepared by a translator registered on the Thai Ministry of Justice's roster, signed and stamped so it is accepted as evidence by Thai courts and by foreign authorities that recognise Thai judicial certification." },
      { q: "Which authorities accept your translations?", a: "Thai courts (civil, criminal, labour, tax, bankruptcy), Office of the Attorney General, Ministry of Foreign Affairs, Department of Business Development, Land Department, Revenue Department, embassies of the US, UK, Australia, Canada, EU, Japan, Korea, Singapore and more." },
      { q: "How long does it take?", a: "Standard 2–5 business days depending on length and complexity. Same-day rush is available for court-imposed deadlines at a 50% surcharge." },
      { q: "Can you legalise the translation after it is done?", a: "Yes. We chain Ministry of Foreign Affairs (นิติกรณ์) certification and destination-embassy attestation in the same case. See our /lgz cluster for the full Apostille and legalisation service." },
      { q: "Do you handle bulk cases for law firms?", a: "Yes — dedicated project manager, weekly progress reports, single monthly invoice, and PDPA-compliant secure file transfer." },
    ],
    ctaCall: "Ready to file a certified MoJ translation?",
    ctaLine: "Call 094-895-8999 · LINE @thainotarylaw · Email — free quote within 15 minutes on business days.",
    breadcrumbHome: "Home",
    breadcrumbMoj: "MoJ Certified Translation",
    seeAllServices: "All 25 MoJ translation packages",
    sections: [
      {
        heading: "Why registered MoJ translators, not any translation vendor",
        paragraphs: [
          "Thailand's Rights and Liberties Protection Department publishes a list of translators authorised to sign certifications for court and administrative use. When a foreign judgment must be recognised in Thailand, or a Thai judgment enforced abroad, that certification is what the receiving court checks first. A translation without the roster number, or signed by a translator not on the list, is routinely returned by court clerks and consular officers.",
          "Our permanent panel of ten registered translators covers Thai↔English, Chinese, Japanese, Korean, Vietnamese, Indonesian and Bahasa Malay. Every certification runs through the firm's Notary Public Services attorneys for a second-layer signature — the same attorneys who counter-sign our Apostille and MFA packages — so a single case can flow from raw source file to fully legalised bundle without changing hands.",
        ],
      },
      {
        heading: "How the workflow actually runs",
        paragraphs: [
          "Step 1 — Intake: send the source file by LINE, email or in-person drop-off. We confirm scope, page count, target language and turnaround inside 15 minutes on business days.",
          "Step 2 — Translation & QA: the registered translator drafts; a bilingual lawyer reviews terminology; you receive a preview PDF for names, dates and monetary amounts.",
          "Step 3 — Certification: translator signs the sworn statement, prints the roster number, and the Notary Public attorney countersigns. Original delivered by messenger, courier or self-pickup at our Sukhumvit office.",
          "Step 4 — Optional legalisation: MFA (นิติกรณ์) → destination-country embassy → Apostille where applicable, all under one case reference.",
        ],
      },
    ],
  },

  zh: {
    hreflang: "zh",
    title: "泰国司法部注册翻译 — 判决书 / 合同 / 公司文件 | Thai Notary Law",
    description:
      "由泰国司法部权利与自由保护司注册翻译员承接:判决书、起诉状、合同、遗嘱、公司章程共25种服务,700-900泰铢/页,2-5个工作日,泰国法院、检察院、外交部认可。",
    h1: "泰国司法部注册翻译认证 — 法院公诉级",
    lead:
      "Thai Notary Law 由列入泰国司法部权利与自由保护司注册册的翻译员执笔,并由本所公证律师(Notary Public Services)双签认证。这正是泰国各级法院、检察院、外交部领事司及各国驻泰使馆所要求的完整链条。2019 年至今累计完成 12,000 余页,涵盖诉讼卷宗、跨境合同、公司章程与遗产文书。",
    benefits: [
      ["司法部在册翻译员", "翻译员姓名列于司法部权利与自由保护司册,注册号打印在每一页。"],
      ["法院级别认证", "翻译宣誓 + 公证律师双签,泰国各级法院、检察院、土地厅、DBD 均直接受理。"],
      ["价格固定 / 出稿快速", "700–900 泰铢/页,2–5 个工作日;开庭紧急案件当日加急可安排。"],
      ["专业法律术语", "双语律师复核民、刑、商、家事术语,按案件建立术语表以保持前后一致。"],
      ["证据链完整", "原件、译稿、认证收据全程记录,交付时附审计级证据包。"],
      ["一站式领事认证", "如需送境外使用,可续办外交部领事司认证及使馆认证/海牙 Apostille。"],
    ],
    faqs: [
      { q: "什么是司法部注册翻译?", a: "由列入泰国司法部注册册的翻译员翻译并宣誓签署的译文,可直接作为泰国法院证据,亦被承认泰国司法认证的境外机关接受。" },
      { q: "哪些机关会接受你们的译文?", a: "泰国各级法院(民、刑、劳动、税务、破产)、检察院、外交部、DBD、土地厅、税务厅,以及美、英、澳、加、欧盟、日、韩、新等国驻泰使馆。" },
      { q: "多长时间可以完成?", a: "标准 2–5 个工作日,视长度与难度而定;法院紧急期限可安排当日加急,加收 50%。" },
      { q: "翻译完可以直接送外交部认证吗?", a: "可以,我们可将外交部领事司认证与目的国使馆认证在同一案件内一并办理,详情参见本站 /lgz 领事认证专区。" },
      { q: "能接批量律所案件吗?", a: "可以,配专属项目经理、周度进度报告、按月统一开票,并采用符合 PDPA 的加密文件传输。" },
    ],
    ctaCall: "需要办理司法部注册翻译?",
    ctaLine: "电话 094-895-8999 · LINE @thainotarylaw · 邮箱咨询 — 工作日 15 分钟内免费报价。",
    breadcrumbHome: "首页",
    breadcrumbMoj: "司法部认证翻译",
    seeAllServices: "全部 25 项司法部翻译服务",
    sections: [
      {
        heading: "为什么必须使用司法部注册翻译员",
        paragraphs: [
          "泰国司法部权利与自由保护司会定期公布获授权承接司法及行政用途认证翻译的翻译员名册。境外判决要在泰国承认执行、泰国判决要送境外执行时,受理机关首先核查的即为该注册号。缺少注册号或翻译员未在名册上的译文,通常会被法院书记官或使馆当场退回。",
          "本所常驻十名注册翻译员,覆盖中、英、日、韩、越、印尼与马来语。所有译文均由公证律师二次签署 —— 与本所办理海牙 Apostille 与外交部认证时相同的律师团队 —— 因此一件案件可从原始文件到完整领事认证不换手直达终点。",
        ],
      },
      {
        heading: "标准办理流程",
        paragraphs: [
          "第一步 — 收件:通过 LINE、邮件或到访交付原件,工作日 15 分钟内确认范围、页数、目的语与交期。",
          "第二步 — 翻译与复核:注册翻译员起稿,双语律师复核术语,交付前提供 PDF 预览确认姓名、日期与金额。",
          "第三步 — 认证签章:翻译员宣誓签署并打印注册号,公证律师二次签署。原件可派送、快递或到访自取。",
          "第四步 — 可选领事认证:外交部(นิติกรณ์)→ 目的国使馆 → Apostille,在同一案号下完成。",
        ],
      },
    ],
  },

  ja: {
    hreflang: "ja",
    title: "タイ司法省登録翻訳 — 判決・契約・会社書類 | Thai Notary Law",
    description:
      "タイ司法省権利自由保護局に登録された翻訳者による認証翻訳。判決書、訴状、契約書、遺言、会社定款など25パッケージ、1ページ700–900バーツ、2–5営業日、タイ裁判所・検察・外務省が受理。",
    h1: "タイ司法省登録翻訳者による認証翻訳",
    lead:
      "Thai Notary Law は、タイ司法省権利自由保護局の登録簿に名前が掲載されている翻訳者と、当事務所の公証弁護士(Notary Public Services)による二重署名で認証翻訳を提供します。これはタイ各裁判所・検察庁・外務省領事局および各国大使館が求める完全な認証チェーンです。2019 年以来 12,000 ページ以上の実績があり、訴訟記録・国際契約・会社定款・相続関連書類に対応します。",
    benefits: [
      ["司法省登録翻訳者", "権利自由保護局の現行登録簿に載る翻訳者。登録番号を全ページに印字。"],
      ["裁判所受理レベル", "翻訳者宣誓 + 公証弁護士副署。タイ裁判所・検察・土地局・DBD 直受理。"],
      ["価格固定・短納期", "1 ページ 700–900 バーツ、2–5 営業日。緊急期日は当日仕上げも対応。"],
      ["法律用語の正確性", "民・刑・商・家事の各分野を双語弁護士が用語レビュー、案件別に用語集を維持。"],
      ["監査対応の証拠パッケージ", "原本・翻訳ドラフト・認証控えをすべて記録し、監査可能な形で納品。"],
      ["海外提出の一括認証", "外務省(นิติกรณ์)+ 相手国大使館認証まで一つの案件で完結。"],
    ],
    faqs: [
      { q: "司法省認証翻訳とは何ですか?", a: "タイ司法省の翻訳者登録簿に登載された翻訳者が翻訳・宣誓し署名した文書で、タイ裁判所の証拠として使用でき、タイ司法認証を認める海外機関にも受理されます。" },
      { q: "どの機関で受理されますか?", a: "タイ各裁判所(民事・刑事・労働・税務・破産)、検察庁、外務省、DBD、土地局、税務局、および米・英・豪・加・EU・日・韓・シンガポール等の駐タイ大使館。" },
      { q: "納期はどのくらいですか?", a: "標準 2–5 営業日。分量と難度によります。裁判期日が迫る場合は当日仕上げ(50%割増)も可能です。" },
      { q: "翻訳後にそのまま外務省認証まで頼めますか?", a: "可能です。外務省領事局認証と相手国大使館認証を同一案件で行います。詳しくは /lgz(領事認証)ページをご参照ください。" },
      { q: "法律事務所からの大量案件は受けますか?", a: "はい、専任 PM、週次進捗報告、月次一括請求、PDPA 準拠の暗号化ファイル授受で対応します。" },
    ],
    ctaCall: "司法省認証翻訳のご依頼はこちら",
    ctaLine: "電話 094-895-8999 · LINE @thainotarylaw · メール — 営業日 15 分以内に無料見積もり。",
    breadcrumbHome: "ホーム",
    breadcrumbMoj: "司法省認証翻訳",
    seeAllServices: "司法省翻訳 全 25 パッケージ",
    sections: [
      {
        heading: "なぜ登録翻訳者でなければならないか",
        paragraphs: [
          "タイ司法省権利自由保護局は、裁判所および行政向けの認証翻訳を扱える翻訳者名簿を公開しています。外国判決をタイで承認執行する場合や、タイ判決を海外で執行する場合、受理機関がまず確認するのがこの登録番号です。番号がない、または名簿に載っていない翻訳者の翻訳は、書記官・領事窓口で差し戻しになるのが通常です。",
          "当事務所は英・中・日・韓・越・尼・馬語をカバーする 10 名の登録翻訳者を常時配置し、公証弁護士による副署まで一貫して社内で完結します。Apostille・外務省認証まで同じ弁護士チームが処理するため、原本入手から領事認証完了までワンストップで進みます。",
        ],
      },
      {
        heading: "標準ワークフロー",
        paragraphs: [
          "ステップ 1 — 受付:LINE・メール・来所いずれかで原本を提出。営業日 15 分以内に対象範囲・ページ数・言語・納期を確定。",
          "ステップ 2 — 翻訳・QA:登録翻訳者が起草、双語弁護士が用語チェック。氏名・日付・金額は事前に PDF プレビューで確認。",
          "ステップ 3 — 認証・署名:翻訳者が宣誓署名し登録番号を印字、公証弁護士が副署。原本は配送・郵送・来所受取から選択。",
          "ステップ 4 — オプション領事認証:外務省(นิติกรณ์)→ 相手国大使館 → Apostille を同一案件番号で完了。",
        ],
      },
    ],
  },

  ko: {
    hreflang: "ko",
    title: "태국 법무부 등록 인증번역 — 판결·계약·법인문서 | Thai Notary Law",
    description:
      "태국 법무부 권리자유보호국 등록 번역사가 수행하는 인증번역. 판결문·소장·계약·유언·법인 정관 등 25개 패키지, 페이지당 700–900바트, 2–5영업일, 태국 법원·검찰·외교부에서 인정.",
    h1: "태국 법무부 등록 번역사 인증번역",
    lead:
      "Thai Notary Law는 태국 법무부 권리자유보호국 등록부에 등재된 번역사와 당 로펌 공증변호사(Notary Public Services)의 이중 서명으로 인증번역을 발급합니다. 태국 각급 법원, 검찰청, 외교부 영사국 및 주태 각국 대사관이 요구하는 완전한 인증 체인입니다. 2019년 이래 12,000페이지 이상 처리, 소송 기록·국제계약·법인 정관·상속 문서를 아우릅니다.",
    benefits: [
      ["법무부 등록 번역사", "권리자유보호국 등록부 등재 번역사. 등록번호를 전 페이지에 인쇄."],
      ["법원 수용 등급", "번역사 선서 + 공증변호사 부서. 태국 법원·검찰·토지청·DBD 즉시 접수."],
      ["가격 고정 / 신속", "페이지당 700–900바트, 2–5영업일. 재판 기일 임박 시 당일 처리 가능."],
      ["법률 용어 정확성", "이중언어 변호사가 민·형·상·가사 용어를 검수하고 사건별 용어집 관리."],
      ["감사 대응 증거 패키지", "원본·번역 초안·인증 접수증을 모두 기록하여 감사 가능한 형태로 인도."],
      ["해외 사용 원스톱", "외교부(นิติกรณ์) + 상대국 대사관 인증 + Apostille를 한 사건으로 진행."],
    ],
    faqs: [
      { q: "법무부 인증번역이란?", a: "태국 법무부 번역사 등록부에 등재된 번역사가 번역·선서·서명한 문서로, 태국 법원의 증거로 사용 가능하며 태국 사법 인증을 인정하는 해외 기관에서도 접수됩니다." },
      { q: "어느 기관이 접수하나요?", a: "태국 각급 법원(민사·형사·노동·조세·파산), 검찰청, 외교부, DBD, 토지청, 국세청 및 미·영·호주·캐나다·EU·일본·한국·싱가포르 등 주태 대사관." },
      { q: "소요 기간은?", a: "표준 2–5영업일. 분량·난이도에 따라 상이하며, 재판 기일이 급박한 경우 당일 처리(50% 할증) 가능." },
      { q: "번역 후 외교부 인증까지 이어서 가능한가요?", a: "가능합니다. 외교부 영사국 인증과 상대국 대사관 인증을 동일 사건으로 진행합니다. 자세한 내용은 /lgz(영사인증) 페이지를 참조하세요." },
      { q: "로펌 대량 사건도 받나요?", a: "네, 전담 PM·주간 진행 보고·월 단위 통합 청구·PDPA 준수 암호화 파일 전송으로 대응합니다." },
    ],
    ctaCall: "법무부 인증번역을 의뢰하시겠습니까?",
    ctaLine: "전화 094-895-8999 · LINE @thainotarylaw · 이메일 — 영업일 15분 이내 무료 견적.",
    breadcrumbHome: "홈",
    breadcrumbMoj: "법무부 인증번역",
    seeAllServices: "법무부 번역 25개 패키지 전체",
    sections: [
      {
        heading: "왜 등록 번역사여야 하는가",
        paragraphs: [
          "태국 법무부 권리자유보호국은 법원·행정 용도 인증번역을 담당할 수 있는 번역사 명단을 공표합니다. 외국 판결을 태국에서 승인·집행하거나 태국 판결을 해외에서 집행할 때, 접수 기관이 가장 먼저 확인하는 것이 이 등록번호입니다. 등록번호가 없거나 명단에 없는 번역사의 번역은 법원 서기·영사 창구에서 반려되는 것이 통상입니다.",
          "당 로펌은 영·중·일·한·베·인·말 언어를 커버하는 등록 번역사 10명을 상시 보유하며, 공증변호사 부서까지 사내에서 완결합니다. Apostille·외교부 인증도 동일한 변호사 팀이 처리하므로 원본 수령부터 영사 인증 완료까지 원스톱으로 진행됩니다.",
        ],
      },
      {
        heading: "표준 업무 흐름",
        paragraphs: [
          "1단계 — 접수: LINE·이메일·방문 중 편한 방법으로 원본 제출. 영업일 15분 이내 범위·페이지·언어·납기 확정.",
          "2단계 — 번역·QA: 등록 번역사 초안, 이중언어 변호사 용어 검수. 성명·일자·금액은 PDF 미리보기로 사전 확인.",
          "3단계 — 인증·서명: 번역사 선서 서명 및 등록번호 인쇄, 공증변호사 부서. 원본은 배송·우편·방문 수령 중 선택.",
          "4단계 — 선택형 영사 인증: 외교부(นิติกรณ์) → 상대국 대사관 → Apostille를 동일 사건번호로 처리.",
        ],
      },
    ],
  },

  vi: {
    hreflang: "vi",
    title: "Dịch công chứng Bộ Tư pháp Thái Lan | Thai Notary Law",
    description:
      "Biên dịch viên đăng ký Bộ Tư pháp: bản án, hợp đồng, di chúc — 25 gói — liên hệ để nhận báo giá, 2–5 ngày, toà án Thái chấp nhận.",
    h1: "Dịch thuật công chứng Bộ Tư pháp Thái Lan",
    lead:
      "Thai Notary Law cung cấp dịch thuật công chứng do biên dịch viên có tên trong danh bạ của Cục Bảo vệ Quyền và Tự do (Bộ Tư pháp Thái Lan) thực hiện, kèm chữ ký kép của luật sư công chứng (Notary Public Services) của hãng. Đây chính là chuỗi xác thực mà toà án Thái Lan, Viện kiểm sát, Cục Lãnh sự Bộ Ngoại giao và các đại sứ quán tại Thái yêu cầu. Từ 2019 đến nay đã hoàn thành hơn 12.000 trang trong các hồ sơ tranh tụng, hợp đồng xuyên biên giới, điều lệ công ty và di sản.",
    benefits: [
      ["Biên dịch viên đăng ký Bộ Tư pháp", "Tên biên dịch trong danh bạ hiện hành của Cục Bảo vệ Quyền và Tự do; số đăng ký in trên mọi trang."],
      ["Chuẩn tòa án", "Tuyên thệ biên dịch + chữ ký luật sư công chứng — toà, viện kiểm sát, sở đất đai, DBD tiếp nhận trực tiếp."],
      ["Giá cố định / nhanh", "Liên hệ để nhận báo giá, 2–5 ngày làm việc; xử lý trong ngày cho phiên toà gấp."],
      ["Chính xác thuật ngữ pháp lý", "Luật sư song ngữ rà soát thuật ngữ dân sự, hình sự, thương mại, hôn nhân; duy trì bảng thuật ngữ theo vụ."],
      ["Chuỗi bằng chứng đầy đủ", "Lưu vết bản gốc, bản dịch nháp, biên nhận công chứng — bàn giao gói đầy đủ cho kiểm toán."],
      ["Hợp pháp hoá quốc tế", "Nối tiếp Bộ Ngoại giao (นิติกรณ์) + đại sứ quán nước đến + Apostille trong cùng một hồ sơ."],
    ],
    faqs: [
      { q: "Dịch thuật công chứng Bộ Tư pháp là gì?", a: "Bản dịch do biên dịch viên có tên trong danh bạ Bộ Tư pháp Thái Lan thực hiện, tuyên thệ và ký; được dùng làm chứng cứ tại toà Thái và được cơ quan nước ngoài công nhận xác thực tư pháp Thái tiếp nhận." },
      { q: "Cơ quan nào chấp nhận bản dịch?", a: "Toà án các cấp Thái Lan (dân sự, hình sự, lao động, thuế, phá sản), Viện kiểm sát, Bộ Ngoại giao, DBD, Sở Đất đai, Cục Thuế và đại sứ quán Mỹ, Anh, Úc, Canada, EU, Nhật, Hàn, Singapore v.v." },
      { q: "Thời gian bao lâu?", a: "Tiêu chuẩn 2–5 ngày làm việc, tuỳ độ dài và độ khó. Hạn toà gấp có thể xử lý trong ngày (phụ phí 50%)." },
      { q: "Có làm luôn hợp pháp hoá lãnh sự sau khi dịch không?", a: "Có. Chúng tôi nối tiếp chứng nhận Cục Lãnh sự và xác nhận đại sứ quán nước đến trong cùng hồ sơ. Xem thêm ở /lgz (hợp pháp hoá lãnh sự)." },
      { q: "Có nhận xử lý số lượng lớn cho hãng luật không?", a: "Có, có PM chuyên trách, báo cáo tiến độ hàng tuần, hoá đơn hàng tháng và truyền tệp mã hoá tuân thủ PDPA." },
    ],
    ctaCall: "Cần dịch thuật công chứng Bộ Tư pháp?",
    ctaLine: "Gọi 094-895-8999 · LINE @thainotarylaw · Email — báo giá miễn phí trong 15 phút giờ hành chính.",
    breadcrumbHome: "Trang chủ",
    breadcrumbMoj: "Dịch thuật công chứng Bộ Tư pháp",
    seeAllServices: "Xem đủ 25 gói dịch Bộ Tư pháp",
    sections: [
      {
        heading: "Vì sao phải dùng biên dịch viên đăng ký",
        paragraphs: [
          "Cục Bảo vệ Quyền và Tự do (Bộ Tư pháp Thái Lan) công bố danh bạ biên dịch viên được uỷ quyền ký xác thực cho toà án và hành chính. Khi công nhận thi hành bản án nước ngoài tại Thái Lan hoặc thi hành bản án Thái Lan ở nước ngoài, cơ quan tiếp nhận đầu tiên kiểm tra số đăng ký này. Bản dịch thiếu số đăng ký hoặc do biên dịch không có tên trong danh bạ thường bị thư ký toà hoặc lãnh sự trả lại.",
          "Chúng tôi thường trực 10 biên dịch viên đăng ký, bao phủ Anh · Trung · Nhật · Hàn · Việt · Indonesia · Mã Lai. Toàn bộ chữ ký kép do luật sư công chứng của hãng thực hiện — cùng đội ngũ xử lý Apostille và hợp pháp hoá — nên một hồ sơ đi thẳng từ bản gốc đến hợp pháp hoá đầy đủ mà không phải chuyển tay.",
        ],
      },
      {
        heading: "Quy trình chuẩn",
        paragraphs: [
          "Bước 1 — Tiếp nhận: gửi bản gốc qua LINE, email hoặc đến văn phòng. Xác nhận phạm vi, số trang, ngôn ngữ và thời hạn trong 15 phút giờ hành chính.",
          "Bước 2 — Dịch & QA: biên dịch viên đăng ký soạn thảo; luật sư song ngữ rà soát thuật ngữ; xem trước PDF để chốt tên, ngày, số tiền.",
          "Bước 3 — Chứng nhận & ký: biên dịch viên tuyên thệ, in số đăng ký; luật sư công chứng ký kép. Bản gốc giao qua chuyển phát, bưu điện hoặc tự nhận tại văn phòng Sukhumvit.",
          "Bước 4 — Tuỳ chọn hợp pháp hoá: Bộ Ngoại giao (นิติกรณ์) → đại sứ quán nước đến → Apostille — trong cùng mã hồ sơ.",
        ],
      },
    ],
  },

  id: {
    hreflang: "id",
    title: "Terjemahan Tersumpah Kemenkumham Thailand | Thai Notary Law",
    description:
      "Penerjemah terdaftar Kementerian Kehakiman: putusan, kontrak, wasiat — 25 paket — hubungi kami untuk penawaran harga, 2–5 hari, diterima pengadilan Thailand.",
    h1: "Terjemahan Tersumpah Kementerian Kehakiman Thailand",
    lead:
      "Thai Notary Law menyediakan terjemahan bersertifikat oleh penerjemah yang namanya tercatat pada daftar Direktorat Perlindungan Hak dan Kebebasan Kementerian Kehakiman Thailand, dengan tanda tangan ganda advokat Notary Public Services kami. Inilah rantai autentikasi lengkap yang diminta oleh pengadilan Thailand, Kejaksaan Agung, Direktorat Konsuler Kementerian Luar Negeri, dan kedutaan negara tujuan di Thailand. Sejak 2019, lebih dari 12.000 halaman telah kami tuntaskan untuk berkas litigasi, kontrak lintas negara, anggaran dasar dan dokumen waris.",
    benefits: [
      ["Penerjemah terdaftar Kementerian Kehakiman", "Nama penerjemah tercantum pada daftar aktif Direktorat Perlindungan Hak dan Kebebasan; nomor registrasi dicetak di setiap halaman."],
      ["Standar pengadilan", "Sumpah penerjemah + tanda tangan advokat notaris — diterima pengadilan, kejaksaan, kantor pertanahan, DBD."],
      ["Harga tetap / cepat", "Hubungi kami untuk penawaran harga, 2–5 hari kerja; layanan hari yang sama tersedia untuk sidang mendesak."],
      ["Presisi terminologi hukum", "Advokat dwi-bahasa mengaudit istilah perdata, pidana, dagang dan keluarga; glosarium khusus per kasus."],
      ["Paket bukti audit-ready", "Semua berkas sumber, draf terjemahan dan tanda terima disimpan dan diserahkan dalam paket siap audit."],
      ["Legalisasi lintas negara", "Menyambung ke Kementerian Luar Negeri (นิติกรณ์) + kedutaan negara tujuan + Apostille dalam satu berkas."],
    ],
    faqs: [
      { q: "Apa itu terjemahan tersumpah Kementerian Kehakiman?", a: "Terjemahan yang dibuat oleh penerjemah yang terdaftar pada daftar Kementerian Kehakiman Thailand, disumpah dan ditandatangani; dapat digunakan sebagai alat bukti di pengadilan Thailand dan diterima oleh lembaga luar negeri yang mengakui sertifikasi yudisial Thailand." },
      { q: "Lembaga mana yang menerima?", a: "Pengadilan Thailand (perdata, pidana, tenaga kerja, pajak, kepailitan), Kejaksaan, Kementerian Luar Negeri, DBD, Kantor Pertanahan, Direktorat Jenderal Pajak, serta kedutaan AS, Inggris, Australia, Kanada, UE, Jepang, Korea, Singapura, dll." },
      { q: "Berapa lama waktunya?", a: "Standar 2–5 hari kerja tergantung volume dan kompleksitas. Untuk tenggat pengadilan mendesak tersedia layanan hari yang sama (biaya tambahan 50%)." },
      { q: "Bisakah langsung dilegalisasi setelah diterjemahkan?", a: "Bisa. Kami merangkai sertifikasi Direktorat Konsuler Kementerian Luar Negeri dan pengesahan kedutaan negara tujuan dalam satu berkas. Lihat halaman /lgz untuk layanan legalisasi." },
      { q: "Apakah menerima proyek besar dari firma hukum?", a: "Ya, dengan project manager khusus, laporan mingguan, tagihan bulanan, dan transfer berkas terenkripsi yang mematuhi PDPA." },
    ],
    ctaCall: "Butuh terjemahan tersumpah Kementerian Kehakiman?",
    ctaLine: "Telepon 094-895-8999 · LINE @thainotarylaw · Email — penawaran gratis dalam 15 menit di jam kerja.",
    breadcrumbHome: "Beranda",
    breadcrumbMoj: "Terjemahan Tersumpah Kemenkeh",
    seeAllServices: "Lihat 25 paket terjemahan Kemenkeh",
    sections: [
      {
        heading: "Mengapa harus penerjemah terdaftar",
        paragraphs: [
          "Direktorat Perlindungan Hak dan Kebebasan Kementerian Kehakiman Thailand menerbitkan daftar penerjemah yang berwenang menandatangani sertifikasi terjemahan untuk kebutuhan yudisial dan administratif. Saat putusan asing hendak diakui atau dijalankan di Thailand, atau sebaliknya, nomor registrasi inilah yang pertama diperiksa oleh instansi penerima. Terjemahan tanpa nomor registrasi, atau oleh penerjemah yang tidak tercantum pada daftar, biasanya dikembalikan oleh panitera pengadilan atau petugas konsuler.",
          "Panel tetap kami terdiri dari 10 penerjemah terdaftar yang mencakup bahasa Inggris, Mandarin, Jepang, Korea, Vietnam, Indonesia dan Melayu. Setiap sertifikasi ditandatangani ganda oleh advokat notaris — tim yang sama yang menangani Apostille dan legalisasi Kementerian Luar Negeri — sehingga satu berkas dapat berjalan langsung dari file sumber hingga paket legalisasi penuh tanpa berpindah tangan.",
        ],
      },
      {
        heading: "Alur kerja standar",
        paragraphs: [
          "Langkah 1 — Intake: kirim berkas sumber via LINE, email, atau kunjungan langsung. Konfirmasi lingkup, jumlah halaman, bahasa target dan tenggat dalam 15 menit di hari kerja.",
          "Langkah 2 — Terjemahan & QA: penerjemah terdaftar membuat draf; advokat dwi-bahasa mengaudit istilah; klien menerima pratinjau PDF untuk nama, tanggal, dan nilai uang.",
          "Langkah 3 — Sertifikasi & tanda tangan: penerjemah bersumpah dan mencetak nomor registrasi, advokat notaris menandatangani ganda. Berkas asli dapat diambil, dikirim melalui kurir atau layanan pos.",
          "Langkah 4 — Legalisasi opsional: Kementerian Luar Negeri (นิติกรณ์) → kedutaan negara tujuan → Apostille — semua dalam satu nomor berkas.",
        ],
      },
    ],
  },
};
